import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ServerCookieService } from '@services';
import { AppStateService } from '@services/app-state.service';
import { ProfileService } from '@services/profile.service';
import { SignInService } from '@services/sign-in.service';
import { SiteConfigService } from '@services/site-config.service';
import { StorageService } from '@services/storage.service';
import {
  FavoritesConfig,
  FavoritesFundRequestType,
  FavoritesFundType,
  FundShareClassId,
} from '@types';
import { OAUTH_TOKEN } from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, of, Subject, combineLatest } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
const logger = Logger.getLogger('FavoritesService');

/**
 * Favorites Service to support different components and different implementations for different sites.
 *
 * TODO May need to make methods product type aware.
 * TODO implement external storage service when available.
 */
@Injectable({
  providedIn: 'root',
})
export class FavoritesService implements OnDestroy {
  private favoritesConfig: FavoritesConfig;
  private favorites: FundShareClassId[] = [];
  private favorites$: BehaviorSubject<FundShareClassId[]> = new BehaviorSubject(
    []
  );

  private favoritesOnLoad$: BehaviorSubject<
    FundShareClassId[]
  > = new BehaviorSubject([]);

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    appStateService: AppStateService,
    private siteConfig: SiteConfigService,
    private storageService: StorageService,
    private profileService: ProfileService,
    private signInService: SignInService,
    private httpClient: HttpClient,
    private appSateService: AppStateService,
    private serverCookieService: ServerCookieService
  ) {
    this.favoritesConfig = appStateService.getFavoritesConfig();
    if (this.favoritesConfig.hasFavorites) {
      this.hasFavorites$().subscribe((hasFavorites) => {
        if (!hasFavorites) {
          this.favorites = [];
          this.favorites$.next(this.favorites);
        }
      });
    }
  }

  isFavouriteEnabled(): boolean {
    return (
      this.favoritesConfig.hasFavorites &&
      this.siteConfig.common?.enableFavoriteFunds
    );
  }

  getFavoriteConfig(): FavoritesConfig {
    return this.favoritesConfig;
  }

  hasFavorites$(): Observable<boolean> {
    if (this.isLoggedInOnly()) {
      return this.profileService.isLoggedIn$();
    }
    return of(this.isFavouriteEnabled());
  }

  getFavorites$(): Observable<FundShareClassId[]> {
    return this.favorites$.asObservable();
  }

  getFavoritesOnLoad$(): Observable<FundShareClassId[]> {
    return this.favoritesOnLoad$.asObservable();
  }

  isFavorite$(fund: FundShareClassId) {
    return this.getFavorites$().pipe(
      map((favorites) => favorites.indexOf(fund) !== -1)
    );
  }

  hideSignModalAfterLogin$(): Observable<boolean> {
    return combineLatest([
      this.signInService.setSignInModal$(),
      this.profileService.isLoggedIn$(),
    ]).pipe(
      map((latestLogin) => {
        const [isSignInModalSet, isProfileLoggedIn] = latestLogin;
        return isSignInModalSet && isProfileLoggedIn;
      })
    );
  }

  toggleFavorite(fund: FundShareClassId): void {
    const index = this.favorites.indexOf(fund);
    const requestBody: FavoritesFundRequestType = {
      favFund: {
        fundId: fund,
        userId: '',
        siteId: this.appSateService.getChannel(),
        envId: this.appSateService.getEnvironment(),
      },
      toDelete: 'false',
    };
    // check whether its a delete or save
    requestBody.toDelete = index !== -1 ? 'true' : 'false';
    this.saveFavorites$(requestBody).subscribe((saved) => {
      if (saved && index !== -1) {
        this.favorites.splice(index, 1); // splice only if condition is true
        this.favorites$.next(this.favorites);
      }
      if (saved && index === -1) {
        this.favorites.push(fund); // push only if condition is true
        this.favorites$.next(this.favorites);
      }
    });
  }

  private isLoggedInOnly(): boolean {
    return this.isFavouriteEnabled() && this.favoritesConfig.loggedInOnly;
  }

  private saveFavorites$(
    requestBody: FavoritesFundRequestType
  ): Observable<boolean> {
    return new Observable((observer) => {
      if (this.favoritesConfig.useLocalStorage) {
        this.storageService.storeFundFavorites(this.favorites);
        observer.next(true);
        observer.complete(); // assuming it will be true always for local starage
      } else {
        this.profileService
          .getUserProfile$()
          .pipe(
            takeUntil(this.unsubscribe$),
            map((data) => data.profileInfo),
            switchMap((profileInfo) => {
              requestBody.favFund.userId = profileInfo.userSysNo;
              return this.setFavoriteFundFromAPI(requestBody);
            })
          )
          .subscribe(
            (response) => {
              if (response?.status === 200) {
                observer.next(true);
                observer.complete(); // will be set true if API response is Success
              }
            },
            (error) => {
              logger.error(error);
              observer.next(false);
              observer.complete();
            }
          );
      }
    });
  }

  public loadFavorites() {
    if (this.favoritesConfig.useLocalStorage) {
      this.storageService.retrieveFundFavorites().then((favorites) => {
        if (favorites) {
          this.favorites = favorites;
        } else {
          this.favorites = [];
        }
        this.favorites$.next(this.favorites);
        this.favoritesOnLoad$.next(this.favorites);
      });
    } else {
      this.profileService
        .getUserProfile$()
        .pipe(
          takeUntil(this.unsubscribe$),
          map((data) => data.profileInfo),
          switchMap((profile) => {
            const data$: Observable<
              FavoritesFundType[]
            > = this.loadFavoriteFundFromAPI(profile.userSysNo);
            return data$;
          })
        )
        .subscribe((responseData) => {
          this.favorites = responseData.map((data) => data.fundId);
          this.favorites$.next(this.favorites);
          this.favoritesOnLoad$.next(this.favorites);
        });
    }
  }
  private loadFavoriteFundFromAPI(
    userSysNo: string
  ): Observable<FavoritesFundType[]> {
    return this.serverCookieService.getCookieValue$(OAUTH_TOKEN).pipe(
      takeUntil(this.unsubscribe$),
      switchMap((authToken: string) => {
        const httpOptions: { [k: string]: HttpHeaders } = {};
        if (this.appSateService.isAPIMUrlEnabledFavFund()) {
          httpOptions.headers = new HttpHeaders({
            'X-FT-API-KEY': this.appSateService.getFavoriteFundApimHeaderKey(),
            'Ocp-Apim-Trace': 'true',
            'Content-Type': 'application/json',
            Authorization: authToken ? `Bearer ${authToken}` : '',
          });
        }

        return this.httpClient.get<FavoritesFundType[]>(
          this.getFavoriteFundApiUrl(userSysNo),
          httpOptions
        );
      })
    );
  }

  private setFavoriteFundFromAPI(
    requestBody: FavoritesFundRequestType
  ): Observable<any> {
    return this.serverCookieService.getCookieValue$(OAUTH_TOKEN).pipe(
      takeUntil(this.unsubscribe$),
      switchMap((authToken: string) => {
        const httpOptions: { [k: string]: any } = {
          observe: 'response',
          responseType: 'text',
        };

        if (this.appSateService.isAPIMUrlEnabledFavFund()) {
          httpOptions.headers = new HttpHeaders({
            'X-FT-API-KEY': this.appSateService.getFavoriteFundApimHeaderKey(),
            'Ocp-Apim-Trace': 'true',
            'Content-Type': 'application/json',
            Authorization: authToken ? `Bearer ${authToken}` : '',
          });
        }

        return this.httpClient.post(
          this.appSateService.toSetFavoriteFundApiUrl(),
          requestBody,
          httpOptions
        );
      })
    );
  }

  private getFavoriteFundApiUrl(userSysNo: string) {
    return this.appSateService.toGetFavoriteFundApiUrl() + '/' + userSysNo;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
